import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import api from '../lib/api'

function formatDate(d) {
  if (!d) return '-'
  const date = new Date(d)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString()
}

function csvEscape(value) {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (/[",\n\r]/.test(str)) {
    return '"' + str.replace(/"/g, '""') + '"'
  }
  return str
}

const DEFAULT_BADGE_BG = '#e9ecef'

export function ContactsPanel({ socket }) {
  const [contacts, setContacts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [contactProgress, setContactProgress] = useState(null) // { totalRemaining } | null
  const [contactLiveStatus, setContactLiveStatus] = useState(null) // 'running'|'done'|'cancelled'|'failed'|null
  const [sentimentProgress, setSentimentProgress] = useState(null)
  const [sentimentLiveStatus, setSentimentLiveStatus] = useState(null)
  const [reclassifyLogs, setReclassifyLogs] = useState([]) // [{ time, contactId, prevType, newType }]
  const [reclassifyTotal, setReclassifyTotal] = useState(0)
  const [toasts, setToasts] = useState([]) // [{ id, text, kind }]

  const pushToast = useCallback((text, kind = 'info') => {
    const id = Date.now() + Math.random()
    setToasts((prev) => [...prev, { id, text, kind }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 5000)
  }, [])
  const [pipelineStats, setPipelineStats] = useState(null) // { contactProcessing, sentimentAnalysis }
  const fetchContactsRef = useRef(null)
  const fetchCountsRef = useRef(null)
  const fetchPipelineStatsRef = useRef(null)
  const [typeFilter, setTypeFilter] = useState('')
  const [accountFilter, setAccountFilter] = useState('')
  const [accounts, setAccounts] = useState([])
  // map of accountId -> { contactCategories, sentimentLabels }
  const [categoriesByAccount, setCategoriesByAccount] = useState({})
  const [page, setPage] = useState(1)
  const [message, setMessage] = useState(null)
  const [analyzingId, setAnalyzingId] = useState(null)
  const [viewingContact, setViewingContact] = useState(null)
  const [analyzingAll, setAnalyzingAll] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [counts, setCounts] = useState({ all: null, byType: {}, analysed: null, notAnalysed: null, stale: null, pending: null, ignore: null })
  const [analysedFilter, setAnalysedFilter] = useState('')
  const [sortBy, setSortBy] = useState('last_contact_date')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const limit = 20

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    api
      .get('/imap-accounts')
      .then(async (res) => {
        const list = res.data || []
        setAccounts(list)
        // fetch categories for each account in parallel
        const entries = await Promise.all(
          list.map((a) =>
            api.get(`/imap-accounts/${a._id}/categories`)
              .then((r) => [a._id, r.data])
              .catch(() => [a._id, null])
          )
        )
        const map = {}
        for (const [id, data] of entries) if (data) map[id] = data
        setCategoriesByAccount(map)
      })
      .catch(() => setAccounts([]))
  }, [])

  const fetchCounts = useCallback(async () => {
    try {
      const params = {}
      if (accountFilter) params.accountId = accountFilter
      if (analysedFilter) params.analysed = analysedFilter
      const res = await api.get('/contacts/counts', { params })
      setCounts(res.data)
    } catch {
      // non-critical
    }
  }, [accountFilter, analysedFilter])

  const fetchPipelineStats = useCallback(async () => {
    try {
      const res = await api.get('/jobs/stats')
      setPipelineStats(res.data)
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => {
    fetchCounts()
  }, [fetchCounts])

  const buildParams = useCallback(
    (extra = {}) => {
      const params = { ...extra }
      if (typeFilter) params.type = typeFilter
      if (accountFilter) params.accountId = accountFilter
      if (analysedFilter) params.analysed = analysedFilter
      if (sortBy) params.sortBy = sortBy
      if (debouncedSearch) params.search = debouncedSearch
      return params
    },
    [typeFilter, accountFilter, analysedFilter, sortBy, debouncedSearch]
  )

  const fetchContacts = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/contacts', { params: buildParams({ page, limit }) })
      setContacts(res.data.contacts)
      setTotal(res.data.total)
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load contacts' })
    } finally {
      setLoading(false)
    }
  }, [page, buildParams])

  useEffect(() => {
    fetchPipelineStats()
  }, [fetchPipelineStats])

  useEffect(() => {
    fetchContactsRef.current = fetchContacts
    fetchCountsRef.current = fetchCounts
    fetchPipelineStatsRef.current = fetchPipelineStats
  })

  useEffect(() => {
    fetchContacts()
  }, [fetchContacts])

  useEffect(() => {
    if (!socket) return

    const onContactProgress = (data) => {
      setContactProgress({ totalRemaining: data.totalRemaining })
      setContactLiveStatus('running')
      fetchPipelineStatsRef.current?.()
    }
    const onContactDone = (data) => {
      setContactProgress(null)
      setContactLiveStatus(data?.status === 'cancelled' ? 'cancelled' : 'done')
      setProcessing(false)
      fetchContactsRef.current?.()
      fetchCountsRef.current?.()
      fetchPipelineStatsRef.current?.()
    }
    const onContactFailed = () => {
      setContactProgress(null)
      setContactLiveStatus('failed')
      setProcessing(false)
      fetchPipelineStatsRef.current?.()
    }

    const onSentimentProgress = (data) => {
      setSentimentProgress({
        totalRemaining: data.totalRemaining,
        batchAnalyzed: data.batchAnalyzed,
        batchReclassified: data.batchReclassified,
      })
      setSentimentLiveStatus('running')
      if (data.batchReclassified) {
        setReclassifyTotal((n) => n + data.batchReclassified)
      }
      if (Array.isArray(data.reclassifications) && data.reclassifications.length) {
        const now = new Date()
        setReclassifyLogs((prev) => [
          ...data.reclassifications.map((r) => ({ time: now, ...r })),
          ...prev,
        ].slice(0, 50))
        const ignored = data.reclassifications.filter((r) => r.newType === 'Ignore')
        if (ignored.length === 1) {
          pushToast(`1 contact marked as Ignore (was "${ignored[0].prevType}")`, 'warning')
        } else if (ignored.length > 1) {
          pushToast(`${ignored.length} contacts marked as Ignore in this batch`, 'warning')
        }
      }
      fetchPipelineStatsRef.current?.()
    }
    const onSentimentDone = (data) => {
      setSentimentProgress(null)
      setSentimentLiveStatus(data?.status === 'cancelled' ? 'cancelled' : 'done')
      setAnalyzingAll(false)
      fetchContactsRef.current?.()
      fetchCountsRef.current?.()
      fetchPipelineStatsRef.current?.()
    }
    const onSentimentFailed = () => {
      setSentimentProgress(null)
      setSentimentLiveStatus('failed')
      setAnalyzingAll(false)
      fetchPipelineStatsRef.current?.()
    }

    socket.on('contact-job-progress', onContactProgress)
    socket.on('contact-job-complete', onContactDone)
    socket.on('contact-job-failed', onContactFailed)
    socket.on('sentiment-job-progress', onSentimentProgress)
    socket.on('sentiment-job-complete', onSentimentDone)
    socket.on('sentiment-job-failed', onSentimentFailed)

    return () => {
      socket.off('contact-job-progress', onContactProgress)
      socket.off('contact-job-complete', onContactDone)
      socket.off('contact-job-failed', onContactFailed)
      socket.off('sentiment-job-progress', onSentimentProgress)
      socket.off('sentiment-job-complete', onSentimentDone)
      socket.off('sentiment-job-failed', onSentimentFailed)
    }
  }, [socket])

  // Build the union of contact category names across the relevant accounts.
  const visibleContactCategories = useMemo(() => {
    const map = new Map()
    const ids = accountFilter ? [accountFilter] : Object.keys(categoriesByAccount)
    for (const id of ids) {
      const cats = categoriesByAccount[id]?.contactCategories || []
      for (const c of cats) {
        if (!map.has(c.name)) map.set(c.name, c)
      }
    }
    return Array.from(map.values())
  }, [accountFilter, categoriesByAccount])

  // Lookup a category record (color etc.) for a given contact, falling back across accounts.
  const findContactCategory = (contact) => {
    const list = categoriesByAccount[contact.accountId]?.contactCategories || []
    return list.find((c) => c.name === contact.type)
  }
  const findSentimentLabel = (contact) => {
    if (!contact.sentiment?.overall) return null
    const list = categoriesByAccount[contact.accountId]?.sentimentLabels || []
    return list.find((l) => l.name === contact.sentiment.overall)
  }

  const handleProcess = async () => {
    setProcessing(true)
    setMessage(null)
    setContactLiveStatus(null)
    setContactProgress(null)
    try {
      await api.post('/contacts/process')
      setContactLiveStatus('running')
    } catch (err) {
      const text = err.response?.data?.error || 'Failed to start processing'
      setMessage({ type: 'error', text })
      setProcessing(false)
    }
  }

  const handleTypeFilter = (value) => {
    setTypeFilter(value)
    setPage(1)
  }

  const handleAccountFilter = (value) => {
    setAccountFilter(value)
    setTypeFilter('')
    setPage(1)
  }

  const handleAnalysedFilter = (value) => {
    setAnalysedFilter(value)
    setPage(1)
  }

  const handleSortBy = (value) => {
    setSortBy(value)
    setPage(1)
  }

  const handleAnalyse = async (contactId) => {
    setAnalyzingId(contactId)
    setMessage(null)
    try {
      const res = await api.post(`/contacts/${contactId}/sentiment`)
      const { sentiment, type, prevType, typeChanged } = res.data
      setContacts((prev) =>
        prev.map((c) => (c._id === contactId ? { ...c, sentiment, ...(type ? { type } : {}) } : c))
      )
      if (typeChanged) {
        setReclassifyTotal((n) => n + 1)
        setReclassifyLogs((prev) =>
          [{ time: new Date(), contactId, prevType, newType: type }, ...prev].slice(0, 50)
        )
        const contact = contacts.find((c) => c._id === contactId)
        const who = contact?.name || contact?.email || 'Contact'
        if (type === 'Ignore') {
          pushToast(`"${who}" was marked as Ignore (was "${prevType}")`, 'warning')
        } else {
          setMessage({ type: 'info', text: `Re-analysed: type changed "${prevType}" → "${type}"` })
        }
        fetchCounts()
      }
    } catch (err) {
      const text = err.response?.data?.error || 'Failed to analyse sentiment'
      setMessage({ type: 'error', text })
    } finally {
      setAnalyzingId(null)
    }
  }

  const handleView = (contact) => setViewingContact(contact)
  const handleCloseModal = () => setViewingContact(null)

  const handleDelete = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name || contact.email}"? This cannot be undone.`)) return
    setDeletingId(contact._id)
    setMessage(null)
    try {
      await api.delete(`/contacts/${contact._id}`)
      setContacts((prev) => prev.filter((c) => c._id !== contact._id))
      setTotal((prev) => prev - 1)
      fetchCounts()
    } catch (err) {
      const text = err.response?.data?.error || 'Failed to delete contact'
      setMessage({ type: 'error', text })
    } finally {
      setDeletingId(null)
    }
  }

  const handleAnalyseAll = async () => {
    setAnalyzingAll(true)
    setMessage(null)
    setSentimentLiveStatus(null)
    setSentimentProgress(null)
    setReclassifyLogs([])
    setReclassifyTotal(0)
    try {
      await api.post('/contacts/sentiments/process')
      setSentimentLiveStatus('running')
    } catch (err) {
      const text = err.response?.data?.error || 'Failed to start sentiment analysis'
      setMessage({ type: 'error', text })
      setAnalyzingAll(false)
    }
  }

  const handleExportCsv = async () => {
    setExporting(true)
    setMessage(null)
    try {
      const res = await api.get('/contacts', {
        params: buildParams({ page: 1, limit: 100000 }),
      })
      const rows = res.data.contacts || []
      const accountMap = new Map(accounts.map((a) => [a._id, a.email]))
      const headers = [
        'Name',
        'Email',
        'Type',
        'Account',
        'First Contact',
        'Last Contact',
        'Contact Count',
        'Sentiment',
        'Sentiment Summary',
      ]
      const lines = [headers.join(',')]
      for (const c of rows) {
        lines.push(
          [
            csvEscape(c.name),
            csvEscape(c.email),
            csvEscape(c.type),
            csvEscape(accountMap.get(c.accountId) || c.accountId || ''),
            csvEscape(c.first_contact_date ? new Date(c.first_contact_date).toISOString() : ''),
            csvEscape(c.last_contact_date ? new Date(c.last_contact_date).toISOString() : ''),
            csvEscape(c.time_contacted_count ?? 0),
            csvEscape(c.sentiment?.overall || ''),
            csvEscape(c.sentiment?.summary || ''),
          ].join(',')
        )
      }
      const csv = lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const today = new Date().toISOString().split('T')[0]
      a.download = `contacts-${today}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export CSV' })
    } finally {
      setExporting(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit))
  const accountLabel = (id) => accounts.find((a) => a._id === id)?.email || id || '-'
  const byTypeCounts = counts.byType || {}

  return (
    <div>
      {toasts.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 360,
          }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              style={{
                background: t.kind === 'warning' ? '#fff4cc' : t.kind === 'error' ? '#fcd7d7' : '#dbe7ff',
                color: '#1a1a2e',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span>{t.text}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                  color: '#1a1a2e',
                  opacity: 0.6,
                }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="contacts-toolbar">
        <div className="job-filters">
          <button className={typeFilter === '' ? 'active' : ''} onClick={() => handleTypeFilter('')}>
            All{counts.all !== null && counts.all !== undefined && <span className="tab-count">{counts.all}</span>}
          </button>
          {visibleContactCategories.map((cat) => (
            <button
              key={cat.name}
              className={typeFilter === cat.name ? 'active' : ''}
              onClick={() => handleTypeFilter(cat.name)}
            >
              {cat.name}
              {byTypeCounts[cat.name] !== undefined && (
                <span className="tab-count">{byTypeCounts[cat.name]}</span>
              )}
            </button>
          ))}
          <button
            className={typeFilter === 'Pending' ? 'active' : ''}
            onClick={() => handleTypeFilter('Pending')}
            title="Contacts awaiting first sentiment analysis (type will be assigned then)"
          >
            Pending
            {counts.pending !== null && counts.pending !== undefined && (
              <span className="tab-count">{counts.pending}</span>
            )}
          </button>
          <button
            className={typeFilter === 'Ignore' ? 'active' : ''}
            onClick={() => handleTypeFilter('Ignore')}
            title="Automated / non-relevant senders (newsletters, no-reply, etc.)"
            style={typeFilter === 'Ignore' ? undefined : { opacity: 0.75 }}
          >
            Ignore
            {counts.ignore !== null && counts.ignore !== undefined && (
              <span className="tab-count">{counts.ignore}</span>
            )}
          </button>
        </div>

        <div className="job-filters">
          <button className={analysedFilter === '' ? 'active' : ''} onClick={() => handleAnalysedFilter('')}>
            All
          </button>
          <button className={analysedFilter === 'true' ? 'active' : ''} onClick={() => handleAnalysedFilter('true')}>
            Analysed{counts.analysed !== null && counts.analysed !== undefined && <span className="tab-count">{counts.analysed}</span>}
          </button>
          <button className={analysedFilter === 'false' ? 'active' : ''} onClick={() => handleAnalysedFilter('false')}>
            Not Analysed{counts.notAnalysed !== null && counts.notAnalysed !== undefined && <span className="tab-count">{counts.notAnalysed}</span>}
          </button>
        </div>

        <div className="contacts-search-wrapper">
          <input
            className="contacts-search-input"
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="contacts-actions">
          <select
            className="contacts-account-select"
            value={sortBy}
            onChange={(e) => handleSortBy(e.target.value)}
          >
            <option value="last_contact_date">Sort: Last Contact</option>
            <option value="first_contact_date">Sort: First Contact</option>
            <option value="time_contacted_count">Sort: Contact Count</option>
            <option value="name">Sort: Name</option>
          </select>
          <select
            className="contacts-account-select"
            value={accountFilter}
            onChange={(e) => handleAccountFilter(e.target.value)}
          >
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a._id} value={a._id}>
                {a.email}
              </option>
            ))}
          </select>
          <button className="btn btn-outline" onClick={() => { fetchContacts(); fetchCounts() }} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button
            className="btn btn-outline"
            onClick={handleExportCsv}
            disabled={exporting || total === 0}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button className="btn btn-primary" onClick={handleProcess} disabled={processing}>
            {processing ? 'Queuing...' : 'Process Contacts'}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAnalyseAll}
            disabled={analyzingAll}
          >
            {analyzingAll ? 'Queuing...' : 'Analyse All Sentiments'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.type}`} style={{ marginBottom: '16px' }}>
          {message.text}
        </div>
      )}

      {pipelineStats && (
        <div className="pipeline-stats-card">
          {(() => {
            const cp = pipelineStats.contactProcessing || {}
            const sa = pipelineStats.sentimentAnalysis || {}
            const cpTotal = cp.totalEmails || 0
            const cpDone = cp.processedEmails || 0
            const cpPct = cpTotal > 0 ? Math.round((cpDone / cpTotal) * 100) : 0
            const saTotal = sa.totalContacts || 0
            const saDone = sa.analyzedContacts || 0
            const saPct = saTotal > 0 ? Math.round((saDone / saTotal) * 100) : 0
            return (
              <>
                <div className="pipeline-stats-row">
                  <div className="pipeline-stats-label-row">
                    <span className="pipeline-stats-title">Contact Processing</span>
                    <span className="pipeline-stats-fraction">
                      {cpDone.toLocaleString()} / {cpTotal.toLocaleString()} emails
                    </span>
                  </div>
                  <div className="pipeline-stats-bar">
                    <div className="pipeline-stats-fill pipeline-stats-fill--contact" style={{ width: `${cpPct}%` }} />
                  </div>
                  <div className="pipeline-stats-sub">
                    <span>{cpPct}% processed</span>
                    <span>{(cp.unprocessedEmails || 0).toLocaleString()} remaining</span>
                  </div>
                </div>

                <div className="pipeline-stats-divider" />

                <div className="pipeline-stats-row">
                  <div className="pipeline-stats-label-row">
                    <span className="pipeline-stats-title">
                      Sentiment Analysis
                      {(sa.staleContacts || 0) > 0 && (
                        <span className="pipeline-stats-stale-badge" title="Contacts with new emails since last analysis">
                          {sa.staleContacts} stale
                        </span>
                      )}
                    </span>
                    <span className="pipeline-stats-fraction">
                      {saDone.toLocaleString()} / {saTotal.toLocaleString()} contacts
                    </span>
                  </div>
                  <div className="pipeline-stats-bar">
                    <div className="pipeline-stats-fill pipeline-stats-fill--sentiment" style={{ width: `${saPct}%` }} />
                  </div>
                  <div className="pipeline-stats-sub">
                    <span>{saPct}% analysed</span>
                    <span>{(sa.unanalyzedContacts || 0).toLocaleString()} remaining</span>
                  </div>
                </div>
              </>
            )
          })()}
        </div>
      )}

      {(contactLiveStatus || sentimentLiveStatus || reclassifyTotal > 0 || reclassifyLogs.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {contactLiveStatus && (
            <div className={`message ${contactLiveStatus === 'running' ? 'info' : contactLiveStatus === 'done' ? 'success' : contactLiveStatus === 'cancelled' ? 'warning' : 'error'}`}>
              {contactLiveStatus === 'running' && (
                <>
                  <span>Processing contacts…</span>
                  {contactProgress?.totalRemaining != null && (
                    <span style={{ marginLeft: 8, opacity: 0.75 }}>{contactProgress.totalRemaining.toLocaleString()} emails remaining</span>
                  )}
                </>
              )}
              {contactLiveStatus === 'done' && 'Contact processing complete.'}
              {contactLiveStatus === 'cancelled' && 'Contact processing cancelled.'}
              {contactLiveStatus === 'failed' && 'Contact processing failed.'}
            </div>
          )}
          {sentimentLiveStatus && (
            <div className={`message ${sentimentLiveStatus === 'running' ? 'info' : sentimentLiveStatus === 'done' ? 'success' : sentimentLiveStatus === 'cancelled' ? 'warning' : 'error'}`}>
              {sentimentLiveStatus === 'running' && (
                <>
                  <span>Analysing sentiments…</span>
                  {sentimentProgress?.totalRemaining != null && (
                    <span style={{ marginLeft: 8, opacity: 0.75 }}>{sentimentProgress.totalRemaining.toLocaleString()} contacts remaining</span>
                  )}
                </>
              )}
              {sentimentLiveStatus === 'done' && 'Sentiment analysis complete.'}
              {sentimentLiveStatus === 'cancelled' && 'Sentiment analysis cancelled.'}
              {sentimentLiveStatus === 'failed' && 'Sentiment analysis failed.'}
            </div>
          )}
          {(reclassifyLogs.length > 0 || reclassifyTotal > 0) && (
            <div
              className="message info"
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                maxHeight: 180,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                Type reclassifications: {reclassifyTotal}
              </div>
              {reclassifyLogs.length === 0 ? (
                <div style={{ opacity: 0.7 }}>No type changes in current batch.</div>
              ) : (
                reclassifyLogs.map((r, i) => (
                  <div key={i} style={{ opacity: 0.85 }}>
                    [{r.time.toLocaleTimeString()}] {r.contactId.slice(-6)}: "{r.prevType}" → "{r.newType}"
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading contacts...</div>
      ) : contacts.length === 0 ? (
        <p className="empty-state">
          No contacts found. Click "Process Contacts" to extract senders from your emails — they'll
          appear as <em>Pending</em>, then "Analyse All Sentiments" assigns each one a type and sentiment.
        </p>
      ) : (
        <>
          <div className="jobs-table-wrapper">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Account</th>
                  <th>First Contact</th>
                  <th>Last Contact</th>
                  <th>Count</th>
                  <th>Sentiment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => {
                  const cat = findContactCategory(c)
                  const sent = findSentimentLabel(c)
                  return (
                    <tr key={c._id}>
                      <td>{c.name || '-'}</td>
                      <td>{c.email || '-'}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            background:
                              c.type === 'Pending'
                                ? '#fff4cc'
                                : c.type === 'Ignore'
                                  ? '#e2e3e5'
                                  : cat?.color || DEFAULT_BADGE_BG,
                            color: '#1a1a2e',
                            fontStyle: c.type === 'Pending' || c.type === 'Ignore' ? 'italic' : 'normal',
                          }}
                        >
                          {c.type}
                        </span>
                      </td>
                      <td>{accountLabel(c.accountId)}</td>
                      <td>{formatDate(c.first_contact_date)}</td>
                      <td>{formatDate(c.last_contact_date)}</td>
                      <td>{c.time_contacted_count ?? 0}</td>
                      <td>
                        {c.sentiment?.overall ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span className="status-badge" style={{ background: sent?.color || DEFAULT_BADGE_BG, color: '#1a1a2e' }}>
                              {c.sentiment.overall}
                            </span>
                            {c.sentiment.stale && (
                              <span className="stale-badge" title="New emails received since last analysis">stale</span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                      <td>
                        <div className="row-actions">
                          <button
                            className="btn btn-outline"
                            onClick={() => handleAnalyse(c._id)}
                            disabled={analyzingId === c._id}
                          >
                            {analyzingId === c._id
                              ? 'Analysing...'
                              : c.sentiment?.overall
                                ? 'Re-analyse'
                                : 'Analyse'}
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => handleView(c)}
                            disabled={!c.sentiment?.overall}
                            title={c.sentiment?.overall ? 'View analysis' : 'No analysis yet'}
                            aria-label="View analysis"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                          <button
                            className="icon-btn icon-btn-danger"
                            onClick={() => handleDelete(c)}
                            disabled={deletingId === c._id}
                            title="Delete contact"
                            aria-label="Delete contact"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="contacts-pagination">
            <span className="pagination-info">
              {total} contact{total !== 1 ? 's' : ''} — page {page} of {totalPages}
            </span>
            <div className="pagination-controls">
              <button
                className="btn btn-outline"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>
              <button
                className="btn btn-outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {viewingContact && (() => {
        const sent = findSentimentLabel(viewingContact)
        return (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="modal-close"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                ×
              </button>
              <h3 style={{ marginBottom: '4px' }}>
                {viewingContact.name || viewingContact.email}
              </h3>
              <div style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
                {viewingContact.email}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <span
                  className="status-badge"
                  style={{ background: sent?.color || DEFAULT_BADGE_BG, color: '#1a1a2e' }}
                >
                  {viewingContact.sentiment?.overall || '—'}
                </span>
              </div>
              <p style={{ marginBottom: '16px' }}>
                {viewingContact.sentiment?.summary || 'No summary available.'}
              </p>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Analysed {viewingContact.sentiment?.analyzed_email_count ?? 0} email
                {viewingContact.sentiment?.analyzed_email_count === 1 ? '' : 's'} ·{' '}
                {formatDate(viewingContact.sentiment?.analyzed_at)}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
